import { parseCmsDocument, type CmsDocument } from '../cms/section-types';
import type { Json } from '../database.types';
import { getSupabaseClient, type DdmSupabaseClient } from '../supabase';

export type CmsPageSlug =
  | 'privacy'
  | 'terms'
  | 'cookies'
  | 'about'
  | 'faq'
  | 'homepage_hero'
  | 'homepage_definition'
  | 'homepage_process'
  | 'homepage_cta'
  | 'trust_pillars'
  | 'contact'
  | 'sell_intro'
  | 'nav'
  | 'footer'
  | 'catalogue_hero';

export type CmsPageLocale = 'en' | 'fr' | 'ar';

export type CmsPage = {
  id: string;
  slug: CmsPageSlug;
  locale: CmsPageLocale;
  title: string;
  body_html: string;
  metadata: Json | null;
  version: number;
  updated_at: string;
};

const COLUMNS = 'id, slug, locale, title, body_html, metadata, version, updated_at';

export async function getCmsPage(
  slug: CmsPageSlug,
  locale: CmsPageLocale,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<CmsPage | null> {
  const { data, error } = await client
    .from('cms_pages')
    .select(COLUMNS)
    .eq('slug', slug)
    .eq('locale', locale)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data as CmsPage;
}

export async function listCmsPages(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<CmsPage[]> {
  const { data, error } = await client
    .from('cms_pages')
    .select(COLUMNS)
    .order('slug', { ascending: true })
    .order('locale', { ascending: true });

  if (error) throw error;
  return (data ?? []) as CmsPage[];
}

/**
 * Fetch the (up to 3) locale rows of a single slug, ordered by locale. The
 * admin editor needs the en/fr/ar trio of a page to hold them in one document
 * each and enforce the structural invariant (ADR-049). A focused alternative to
 * `listCmsPages()` when only one slug is being edited.
 */
export async function getCmsPagesForSlug(
  slug: CmsPageSlug,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<CmsPage[]> {
  const { data, error } = await client
    .from('cms_pages')
    .select(COLUMNS)
    .eq('slug', slug)
    .order('locale', { ascending: true });

  if (error) throw error;
  return (data ?? []) as CmsPage[];
}

export async function updateCmsPage(
  id: string,
  patch: { title?: string; body_html?: string; metadata?: Json | null },
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<CmsPage> {
  const { data, error } = await client
    .from('cms_pages')
    .update(patch)
    .eq('id', id)
    .select(COLUMNS)
    .single();

  if (error) throw error;
  return data as CmsPage;
}

/**
 * Fetch a page and parse its `metadata` into the typed sections document
 * (schema_version 2). Returns null when the page is absent or its metadata is
 * legacy/invalid, so public callers fall back to their i18n defaults.
 */
export async function getCmsDocument(
  slug: CmsPageSlug,
  locale: CmsPageLocale,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<CmsDocument | null> {
  const page = await getCmsPage(slug, locale, client);
  return page ? parseCmsDocument(page.metadata) : null;
}

/**
 * Atomically write the typed sections document of a page across its en/fr/ar
 * rows via the `update_cms_document` RPC (SECURITY INVOKER, admin-only through
 * the cms_pages UPDATE RLS policy). The single statement keeps the three rows
 * structurally aligned (ADR-049) — a partial write that drifts one locale is
 * impossible. The admin editor is the sole caller; it never uses
 * `updateCmsPage` (a per-locale write would break the invariant).
 *
 * The `CmsDocument → Json` cast is safe: a CmsDocument is plain JSON-serialisable
 * data, and the editor always validates the three documents with
 * `cmsDocumentSchema` before calling this (the DB metadata-shape CHECK is only a
 * coarse guard).
 */
export async function updateCmsDocument(
  slug: CmsPageSlug,
  docs: Record<CmsPageLocale, CmsDocument>,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { error } = await client.rpc('update_cms_document', {
    p_slug: slug,
    p_en: docs.en as unknown as Json,
    p_fr: docs.fr as unknown as Json,
    p_ar: docs.ar as unknown as Json,
  });
  if (error) throw error;
}
